import {
  Block,
  getEditorBlockConfig,
} from "@babackapp/editor-blocks-library/blocks";
import { cn } from "@babackapp/ui/lib/utils";
import { useTranslation } from "react-i18next";
import { EyeOff } from "lucide-react";
import { motion } from "motion/react";
import { useMemo } from "react";
import { useEditorSelectedBlock } from "../contexts/editor-selected-block-context";
import {
  useCustomizedBlocks,
  useEditorContext,
  useHiddenBlocks,
} from "./editor";

type BlockContainerProps = React.PropsWithChildren<{
  block: Block;
  section: "before" | "inside" | "after";
  index: number;
}>;

export default function BlockContainer({
  children,
  section,
  index: _index,
  block,
}: BlockContainerProps) {
  const { selectedBlock, setSelectedBlock, hoveredBlock, setHoveredBlock } =
    useEditorSelectedBlock();
  const isSelected = !!selectedBlock && selectedBlock.id === block.id;
  const isHovered = hoveredBlock?.id === block.id;
  const blockConfig = getEditorBlockConfig(block.type);
  const BlockIcon = blockConfig?.config?.Icon;
  const { theme, editorMode } = useEditorContext();
  const { isHidden } = useHiddenBlocks();
  const { isCustomized } = useCustomizedBlocks();

  const isWorkflowMode = editorMode === "workflowCustom";
  const blockIsHidden = isWorkflowMode && isHidden(block.id);
  const blockIsCustomized = isWorkflowMode && isCustomized(block.id);

  const handleClick = () => {
    setSelectedBlock(block);
  };

  const iconColors = useMemo(() => {
    if (!theme)
      return {
        background: "#ffffff",
        icon: "#000000",
      };

    if (section === "inside") {
      return {
        background: theme.cardTextColor || "#ffffff",
        icon: theme.cardColor || "#000000",
      };
    } else {
      return {
        background: theme.backgroundTextColor || "#ffffff",
        icon: theme.backgroundColor || "#000000",
      };
    }
  }, [theme, section]);

  return (
    <div
      onClick={handleClick}
      className={cn(
        "relative ring ring-transparent relative rounded transition-all",
      )}
      onMouseEnter={() => setHoveredBlock(block)}
    >
      {!!BlockIcon && !blockIsHidden && (
        <div
          className={cn(
            "absolute top-0 left-0 flex h-6 w-6 items-center justify-center rounded shadow ring opacity-0 pointer-events-none transition-opacity",
            {
              "opacity-90": isHovered,
            },
          )}
          style={{
            backgroundColor: iconColors.background,
            color: iconColors.icon,
          }}
        >
          <BlockIcon className="size-4" style={{ color: iconColors.icon }} />
        </div>
      )}
      {isSelected && <SelectionRing />}
      {isHovered && !isSelected && <HoverRing />}
      {blockIsCustomized && !blockIsHidden && <CustomizedIndicator />}
      {blockIsHidden && <HiddenBlockOverlay />}
      {children}
    </div>
  );
}

function SelectionRing() {
  return (
    <motion.div
      layoutId="block-selection-ring"
      className="absolute rounded-lg -inset-2 ring ring-ring"
      initial={{ scale: 1, borderWidth: 1 }}
      transition={{ type: "spring", mass: 0.5 }}
    />
  );
}

function HoverRing() {
  return (
    <motion.div
      className="absolute rounded-lg -inset-2 ring ring-ring/10"
      initial={{ scale: 0.95 }}
      animate={{ scale: 1 }}
    />
  );
}

function CustomizedIndicator() {
  const { t } = useTranslation("trackoffice");
  return (
    <div className="absolute rounded-lg -inset-2 ring-2 ring-amber-400/50 pointer-events-none">
      <span className="absolute bottom-1 right-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
        {t("blocksEditor.customized", "Customized")}
      </span>
    </div>
  );
}

function HiddenBlockOverlay() {
  const { t } = useTranslation("trackoffice");
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center rounded bg-muted/60 backdrop-blur-[1px] pointer-events-none">
      <div className="flex items-center gap-2 text-muted-foreground">
        <EyeOff className="size-4" />
        <span className="text-xs font-medium">
          {t("blocksEditor.hiddenBlock", "Hidden block")}
        </span>
      </div>
    </div>
  );
}
